import { useEffect, useMemo, useRef, useState } from 'react'

// Line height used when capping the visible height to `maxLines` (must match the
// container's line-height so the cap lands on an exact line boundary).
const LINE_HEIGHT_EM = 1.5

interface TypewriterTextProps {
  /** The full text to reveal. */
  text: string
  /** Delay between each revealed letter, in ms. */
  charDelayMs?: number
  /** Cap the visible height to this many lines; overflow scrolls (follows the typing). */
  maxLines?: number
  className?: string
}

/**
 * Reveals `text` one letter at a time (a typewriter effect). Restarts whenever the
 * component mounts or the text changes. When `maxLines` is set, the text is capped
 * to that many lines and scrolls (auto-following the newest characters).
 */
const TypewriterText: React.FC<TypewriterTextProps> = ({ text, charDelayMs = 70, maxLines, className = '' }: TypewriterTextProps) => {
  const chars = useMemo(() => Array.from(text), [text])
  const [visibleCount, setVisibleCount] = useState<number>(0)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setVisibleCount(0)
    if (chars.length === 0) return

    const id = setInterval(() => {
      setVisibleCount((prev) => {
        const next = prev + 1
        if (next >= chars.length) clearInterval(id)
        return Math.min(next, chars.length)
      })
    }, charDelayMs)

    return () => clearInterval(id)
  }, [chars, charDelayMs])

  // Keep the latest typed characters in view when the text scrolls past `maxLines`.
  useEffect(() => {
    const el = containerRef.current
    if (el !== null) el.scrollTop = el.scrollHeight
  }, [visibleCount])

  const style = maxLines !== undefined
    ? { lineHeight: `${LINE_HEIGHT_EM}em`, maxHeight: `${maxLines * LINE_HEIGHT_EM}em`, overflowY: 'auto' as const }
    : undefined

  return <div ref={containerRef} data-testid='typewriter-text' className={className} style={style}>{chars.slice(0, visibleCount).join('')}</div>
}

export default TypewriterText
